import { Temporal } from 'temporal-polyfill'

import type { ScheduleEvent } from '../types'
import { ScheduleEventHelper } from './scheduleEventHelper'

/**
 * Process an array of schedule events and return ScheduleEventHelper instances
 * filtered to exclude events that have ended
 */
export const processScheduleEvents = (
  events: ScheduleEvent[],
  scheduleTimeZone: string,
): ScheduleEventHelper[] => {
  const helpers: ScheduleEventHelper[] = []

  for (const event of events) {
    try {
      const helper = new ScheduleEventHelper(event, scheduleTimeZone)
      if (!helper.hasEventEnded()) {
        helpers.push(helper)
      }
    } catch {
      // Ignore malformed event payloads instead of failing the entire schedule UI.
      continue
    }
  }

  return helpers
}

/**
 * Get weekly hours events (fbtype === 'FREE')
 */
export const getWeeklyHoursEvents = (
  helpers: ScheduleEventHelper[],
): ScheduleEventHelper[] => {
  return helpers.filter((helper) => helper.isWeeklyHoursEvent())
}

/**
 * Get override events (fbtype !== 'FREE'), sorted by start date
 */
export const getOverrideEvents = (
  helpers: ScheduleEventHelper[],
): ScheduleEventHelper[] => {
  return helpers
    .filter((helper) => helper.isOverrideEvent())
    .sort((a, b) =>
      Temporal.ZonedDateTime.compare(
        a.zonedDateTimeStart,
        b.zonedDateTimeStart,
      ),
    )
}

/**
 * Get non-holiday override events, sorted by start date
 */
export const getCustomOverrideEvents = (
  helpers: ScheduleEventHelper[],
): ScheduleEventHelper[] => {
  return getOverrideEvents(helpers).filter((helper) => !helper.isHolidayEvent())
}

/**
 * Get holiday override events, sorted by start date
 */
export const getHolidayOverrideEvents = (
  helpers: ScheduleEventHelper[],
): ScheduleEventHelper[] => {
  return getOverrideEvents(helpers).filter((helper) => helper.isHolidayEvent())
}
